import React, { useState, useEffect, useMemo } from "react";
import { User } from "lucide-react";
import { useAuth } from "../../store/hooks/useAuth";
import {
  useGetTodayStatusQuery,
  useGetCalendarDataQuery,
} from "../../store/apis/attendance.api";
import { useGetMyLeaveBalanceQuery } from "../../store/apis/leave.api";
import { useGetMyTimesheetsQuery } from "../../store/apis/timesheet.api";
import HolidayCard from "../holiday/HolidayCard";
import EmployeeCard from "./EmployeeCard";
import BirthdayAnniversaryCard from "./BirthdayAnniversaryCard";
import RecentJoinersCard from "./RecentJoinersCard";
import LeaveSummaryCard from "./LeaveSummaryCard";
import DashboardHero from "./DashboardHero";
import QuickActions from "./QuickActions";
import ThisWeekStrip from "./ThisWeekStrip";
import RecentActivity from "./RecentActivity";
import AttendanceSnapshot from "./AttendanceSnapshot";
import StatsCards from "../common/StatsCards";
import { RoleTypeEnum } from "../../utils/constants";
import { formatDate } from "../../utils/timeUtils";
import {
  BirthdayAnniversaryCardSkeleton,
  HolidaysCardSkeleton,
  MonthlyJoinersSkeleton,
  QuickStatsSkeleton,
  TeamStatusSkeleton,
} from "./Skeleton";
import { capitalizeWords } from "../../utils/nameUtils";
import { useGetUserByIdQuery } from "../../store/apis/user.api";
import { useSignedUrl } from "../../store/hooks/useSignedUrl";
import { SimpleTooltip } from "../common";
import { getStatusCategory } from "../../constants/attendance";
import { CARD_CLASS, QUICK_STAT_CONFIGS } from "../../constants/dashboard";

// ✅ 1. NATIVE BRIDGE TYPE DEFINITION
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin =
    user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN.toLowerCase();
  const [imgError, setImgError] = useState(false);

  const { data: employeeData, isLoading: loadingEmployee } = useGetUserByIdQuery(
    user?.id || "",
    { skip: !user?.id }
  );
  const profilePicSrc = useSignedUrl(employeeData?.profilePic);
  const loading = loadingEmployee;
  const { data: todayStatusResponse } = useGetTodayStatusQuery(undefined, {
    skip: !user?.id,
  });
  const [_attendanceStatus, setAttendanceStatus] = useState<string>("checked_out");

  // ✅ NATIVE BRIDGE SYNC – uses v2 attendance today-status API
  useEffect(() => {
    if (!user?.id) return;
    const statusData = todayStatusResponse?.data;
    const isActive = !!statusData?.checkInTime && !statusData?.checkOutTime;
    const currentStatus = isActive ? "checked_in" : "checked_out";
    setAttendanceStatus(currentStatus);

    const bridge = window.ReactNativeWebView;
    if (bridge) {
      const messageType = isActive
        ? "START_MOBILE_SERVICES"
        : "STOP_MOBILE_SERVICES";
      bridge.postMessage(
        JSON.stringify({
          type: messageType,
          payload: {
            status: currentStatus,
            checkInTime: statusData?.checkInTime ?? null,
            source: "dashboard_sync",
          },
        })
      );
      console.log(`📦 NATIVE BRIDGE: Projection Sync Fired -> ${messageType}`);
    }
  }, [user?.id, todayStatusResponse]);

  // ============ QUICK STATS (all users) ============
  const now = new Date();
  const calendarMonth = now.getMonth() + 1;
  const calendarYear = now.getFullYear();

  const dayIdx = now.getDay();
  const diffToMonday = dayIdx === 0 ? -6 : 1 - dayIdx;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMonday);
  const friday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 4);

  const { data: leaveBalance, isLoading: loadingBalance } =
    useGetMyLeaveBalanceQuery();
  const { data: calendarResponse, isLoading: loadingCalendar } =
    useGetCalendarDataQuery({ month: calendarMonth, year: calendarYear });
  const { data: timesheetData, isLoading: loadingTimesheets } =
    useGetMyTimesheetsQuery({
      startDate: toDateStr(monday),
      endDate: toDateStr(friday),
      limit: 50,
    });

  const { topLeaveLabel, quickStats } = useMemo(() => {
    const balances = leaveBalance?.balancesMap || {};
    const totalLeave = Object.values(balances).reduce(
      (sum, value) => sum + Number(value || 0),
      0
    );

    // Multi-tenant leave selection:
    // 1. Highest available balance (> 0)
    // 2. If all balances are 0, picks the primary leave type of that organization (e.g., Casual Leave: 00)
    // 3. Fallback if tenant has 0 leave types configured: defaults to "Leave Credit" (00)
    const types = leaveBalance?.types || [];
    const sortedTypes = [...types].sort(
      (a, b) => Number(b.available || 0) - Number(a.available || 0)
    );
    const primaryLeave = sortedTypes.length > 0 ? sortedTypes[0] : null;
    const dynamicLeaveLabel = primaryLeave?.label || "Leave Credit";
    const dynamicLeaveValue = primaryLeave ? Math.max(0, Number(primaryLeave.available || 0)) : 0;

    const days = calendarResponse?.data?.days || [];
    let present = 0;
    for (const d of days) {
      const cat = d.status ? getStatusCategory(d.status) : "none";
      if (cat === "present") present++;
    }

    const loggedHours = (timesheetData?.data || []).reduce(
      (sum, t) => sum + Number(t.totalHours || 0),
      0
    );

    return {
      topLeaveLabel: dynamicLeaveLabel,
      quickStats: {
        leaveBalance: String(totalLeave),
        presentDays: String(present),
        loggedHours: `${Math.round(loggedHours)}h`,
        dynamicLeave: String(dynamicLeaveValue),
      },
    };
  }, [leaveBalance, calendarResponse, timesheetData]);

  const quickLoading = loadingBalance || loadingCalendar || loadingTimesheets;

  const capitalize = (value = "") =>
    value
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* HERO */}
      <DashboardHero
        firstName={employeeData?.firstName}
        loading={loading}
        todayStatus={todayStatusResponse?.data}
      />

      {/* QUICK STATS — shown for all users */}
      {quickLoading ? (
        <QuickStatsSkeleton />
      ) : (
        <StatsCards
          stats={quickStats}
          cards={[
            {
              key: "leaveBalance",
              label: "Leave Balance",
              icon: QUICK_STAT_CONFIGS[0].Icon,
              iconBg: QUICK_STAT_CONFIGS[0].iconBg,
              iconColor: QUICK_STAT_CONFIGS[0].iconColor,
              description: QUICK_STAT_CONFIGS[0].sub,
            },
            {
              key: "presentDays",
              label: "Present Days",
              icon: QUICK_STAT_CONFIGS[1].Icon,
              iconBg: QUICK_STAT_CONFIGS[1].iconBg,
              iconColor: QUICK_STAT_CONFIGS[1].iconColor,
              description: QUICK_STAT_CONFIGS[1].sub,
            },
            {
              key: "loggedHours",
              label: "Logged Hours",
              icon: QUICK_STAT_CONFIGS[2].Icon,
              iconBg: QUICK_STAT_CONFIGS[2].iconBg,
              iconColor: QUICK_STAT_CONFIGS[2].iconColor,
              description: QUICK_STAT_CONFIGS[2].sub,
            },
            {
              key: "dynamicLeave",
              label: topLeaveLabel,
              icon: QUICK_STAT_CONFIGS[3].Icon,
              iconBg: QUICK_STAT_CONFIGS[3].iconBg,
              iconColor: QUICK_STAT_CONFIGS[3].iconColor,
              description: "available",
            },
          ]}
          variant="simple-icon"
          valueOnTop
          gridCols="lg:grid-cols-4"
        />
      )}

      {/* QUICK ACTIONS */}
      <QuickActions />

      {/* THIS WEEK */}
      <ThisWeekStrip />

      {/* RECENT ACTIVITY | LEAVE SUMMARY + HOLIDAYS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 items-stretch">
        <div className="lg:col-span-2 min-w-0">
          <RecentActivity />
        </div>
        <div className="flex flex-col gap-3 sm:gap-4 min-w-0 lg:grid lg:grid-rows-2 lg:h-[480px]">
          <div className="min-h-0 flex flex-col lg:h-full">{loading ? <TeamStatusSkeleton /> : <LeaveSummaryCard />}</div>
          <div className="min-h-0 flex flex-col lg:h-full">{loading ? <HolidaysCardSkeleton /> : <HolidayCard />}</div>
        </div>
      </div>

      {/* BIRTHDAYS & ANNIVERSARIES — carousel tile row */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6">
        {loading ? <BirthdayAnniversaryCardSkeleton /> : <BirthdayAnniversaryCard />}
      </div>

      {/* THREE-CARD ROW: TEAM STATUS | RECENT JOINERS | ATTENDANCE SNAPSHOT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        <div className="h-[300px] flex flex-col min-w-0">
          {loading ? <TeamStatusSkeleton /> : <EmployeeCard />}
        </div>

        <div className="h-[300px] flex flex-col min-w-0">
          {loading ? <MonthlyJoinersSkeleton /> : <RecentJoinersCard mode="joiners-only" />}
        </div>

        <div className="h-[300px] flex flex-col min-w-0">
          <AttendanceSnapshot />
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
